import { create } from "zustand";
import type { AskResponse } from "@/lib/api";
import type { ChartSpec } from "@/lib/chartSpec";
import type { VizRenderPayload } from "@/lib/viz/types";

export type TurnStatus = "streaming" | "complete" | "error";

/**
 * Which backend pipeline answered this turn:
 *  - "mcp": agentic loop — LLM chose tools, gateway called MCP server(s)
 *  - "api": legacy direct-SQL path (SqlGenerator → SqlExecutor → Narrator)
 *  - "unknown": not yet determined (still streaming, or no disambiguating
 *    event was emitted — e.g. clarification-only turn)
 */
export type TurnSource = "mcp" | "api" | "unknown";

export type ToolStatus = "pending" | "ok" | "error";

export type ToolVisual = { callId: string; mimeType: string; dataUrl: string };
export type ToolResource = { callId: string; url: string; label: string };
export type ToolChart = { callId: string; spec: ChartSpec };

/**
 * Server-emitted canonical table — the gateway extracts a tabular
 * structured-block from a tool result and forwards it as a separate
 * event. The UI renders this with {@code ResultsTable} so the rows are
 * deterministic regardless of what the LLM narrates around them. See
 * {@code GatewayEvent.ToolTable} on the server side.
 */
export type ToolTable = {
  callId: string;
  columnNames: string[];
  rows: unknown[][];
  rowCount: number;
};
export type ToolInvocation = {
  callId: string;
  tool: string;
  argumentsJson?: string;
  status: ToolStatus;
  errorMessage?: string;
};

export type ChatTurn = {
  id: string;
  question: string;
  status: TurnStatus;
  source?: TurnSource;
  rewritten?: string;
  sql?: string;
  clarification?: string;
  rejected?: { violations: string[] };
  rows?: { columnNames: string[]; rows: unknown[][]; rowCount: number };
  narrative?: AskResponse["narrative"];
  errorMessage?: string;
  toolCalls?: ToolInvocation[];
  visuals?: ToolVisual[];
  resources?: ToolResource[];
  charts?: ToolChart[];
  /**
   * Canonical tables emitted by the gateway when a tool returns
   * tabular data. Distinct from {@link rows} (the legacy SQL pipeline's
   * result set) — agentic answers can contain multiple tool tables
   * per turn, so this is an array.
   */
  tables?: ToolTable[];
  /**
   * Rich chart from the viz pipeline (`/api/v1/visualize`). When
   * present the MessageCard renders a {@code VisualBlock} that
   * delegates to the format-specific renderer (vega-lite / echarts /
   * deck-gl) AND surfaces the three follow-up affordances ("Refine
   * this chart" / "Ask about this data" / "Re-run with filters").
   */
  vizSpec?: VizRenderPayload | null;
  /**
   * The DatasetRef that produced {@link vizSpec}, when the chart came
   * from `cognos.report.run`. Captured so the "Re-run with filters"
   * modal can re-issue the fetch with different parameter values.
   * Inline-source charts (e.g. follow-up re-styles) don't carry one
   * because there's nothing to re-fetch.
   */
  sourceRef?: { kind: string; params: Record<string, unknown> } | null;
};

/**
 * Pinned scope sent alongside every ask in this session. Maps 1:1 to
 * the gateway's {@code context.primaryObject} field so the LLM can read
 * it as structured context (and so the {@code getFolderActivity} /
 * {@code getPackageActivity} tools know which scopePath to use without
 * the user re-typing it in prose).
 */
export type ChatScope = {
  path: string;
  name: string;
  objectClass: string;
};

type ChatState = {
  sessionId?: string;
  scope?: ChatScope;
  turns: ChatTurn[];
  addTurn: (turn: ChatTurn) => void;
  updateTurn: (id: string, patch: Partial<ChatTurn>) => void;
  setSessionId: (id: string) => void;
  setTurns: (turns: ChatTurn[]) => void;
  setScope: (scope: ChatScope | undefined) => void;
  resetConversation: () => void;
};

export const useChatStore = create<ChatState>((set) => ({
  sessionId: undefined,
  scope: undefined,
  turns: [],
  addTurn: (turn) => set((s) => ({ turns: [...s.turns, turn] })),
  updateTurn: (id, patch) =>
    set((s) => ({
      turns: s.turns.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    })),
  setSessionId: (id) => set({ sessionId: id }),
  setTurns: (turns) => set({ turns }),
  setScope: (scope) => set({ scope }),
  resetConversation: () => set({ sessionId: undefined, turns: [], scope: undefined }),
}));
